/**
 * Desktop notification utilities for incoming calls
 */

// Check if notifications are supported
export const isNotificationSupported = () => 'Notification' in window;

// Get current notification permission status
export const getNotificationPermission = () =>
  !isNotificationSupported() ? 'unsupported' : Notification.permission;

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn('Notifications are not supported in this browser');
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('Notifications are blocked by user');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

// Initialize notification permissions on app load
export const initializeNotifications = async () => {
  const permission = await requestNotificationPermission();

  if (permission === 'granted') {
    console.log('✅ Desktop notifications enabled');
    return true;
  }
  if (permission === 'denied') {
    console.warn('❌ Desktop notifications blocked');
    return false;
  }
  console.log('⏳ Desktop notifications permission pending');
  return false;
};

// Create a call notification
export const createCallNotification = (call, options = {}) => {
  if (getNotificationPermission() !== 'granted') {
    console.warn('Cannot create notification: permission not granted');
    return null;
  }

  const defaultOptions = {
    body: `${call.callerName || 'Unknown Caller'} is calling...\n${call.callerNumber || 'Private Number'}`,
    icon: '/favicon.ico',
    tag: `call-${call.id}`,
    renotify: true,
    requireInteraction: true,
    silent: false,
    badge: '/favicon.ico',
    timestamp: Date.now(),
    actions: [
      {
        action: 'answer',
        title: '📞 Answer',
        icon: '/favicon.ico',
      },
      {
        action: 'decline',
        title: '❌ Decline',
        icon: '/favicon.ico',
      },
    ],
    ...options,
  };

  try {
    const notification = new Notification('📞 Incoming Call', defaultOptions);

    // Auto-close after 30 seconds
    setTimeout(() => {
      if (notification && !notification.closed) {
        notification.close();
      }
    }, 30000);

    return notification;
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
};

// Close all call notifications
export const closeAllCallNotifications = () => {
  // Close the stored notification reference
  if (window.currentCallNotification) {
    window.currentCallNotification.close();
    window.currentCallNotification = null;
  }
};

// Check if window/tab is in background
export const isWindowInBackground = () =>
  document.visibilityState !== 'visible' || document.hidden || !document.hasFocus();

// Setup notification permission request on user interaction
export const setupNotificationPermissionRequest = () => {
  if (!isNotificationSupported() || getNotificationPermission() !== 'default') {
    return;
  }

  // Request permission on first user interaction
  const requestOnInteraction = () => {
    requestNotificationPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('✅ Notification permission granted after user interaction');
      }
    });

    // Remove listeners after first interaction
    document.removeEventListener('click', requestOnInteraction);
    document.removeEventListener('keydown', requestOnInteraction);
    document.removeEventListener('touchstart', requestOnInteraction);
  };

  document.addEventListener('click', requestOnInteraction, { once: true });
  document.addEventListener('keydown', requestOnInteraction, { once: true });
  document.addEventListener('touchstart', requestOnInteraction, { once: true });
};
